import { EventNames, FocusTrap, inertHelper, isMobile, ScrollLock, Swipeable, SwipeDirection } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { Key } from 'ts-key-enum';

import { TrackingType } from '../../../../Commerce/TrackingType.csharp';
import { PictureProfile } from '../../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../../Resources/Translations';
import { showFatalErrorModal } from '../../../Partials/Header/FatalErrorModal';
import { getFor } from '../../../ReactComponents/API';
import { RecommendationsService } from '../../../Recommendations/ViewModels/RecommendationsService';
import { GoogleTagManager } from '../../../Tracking/GoogleTagManager/GoogleTagManager';
import { SetQuantityResult } from '../../Checkout/Summary/LineItemsApi';
import { showRxSessionExpiredModal } from '../../Rx/Modals/RxSessionExpiredModal';
import { CartDrawerSettingsPageViewModel } from '../CartDrawer/Page/CartDrawerSettingsPageViewModel.csharp';
import { CartAccessor } from '../Components/CartAccessor';
import { CartSummary } from '../Components/CartSummary/CartSummary';
import { removeFromCart, setQuantity } from '../Components/CartUtils';
import { EmptyCart } from '../Components/EmptyCart/EmptyCart';
import { PersonalizedCartViewModel } from '../PersonalizedCart/PersonalizedCartViewModel.csharp';
import { CartViewModel } from '../ViewModels/CartViewModel.csharp';

interface MousePosition {
    x: number;
    y: number;
}

export interface CartV1Props {
    cartUrl: string;
    cartItemUrl: string;
    couponUrl: string;
    cartSummaryUrl: string;
    pictureProfile: PictureProfile;
    displayCart: boolean;
    cartDrawerSettings: CartDrawerSettingsPageViewModel;
    showButtonLabel: boolean;
    buttonLabel: string;
}

export interface CartV1State {
    isVisible: boolean;
    isAnimationOn: boolean;
    cart: CartViewModel | null;
    itemsTotalQuantity: number;
    lastUpdatedItem: string;
    isLoaderActive: boolean;
}

export class CartV1 extends React.Component<CartV1Props, CartV1State> {
    private readonly cartContainerRef = React.createRef<HTMLDivElement>();
    private readonly cartRef = React.createRef<HTMLDivElement>();
    private readonly quantityBagdeRef = React.createRef<HTMLDivElement>();
    private quantityBagdeAnimationTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();
    private hideHeaderTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();
    private hideHeaderAfterAnimationRef = React.createRef<boolean>();
    private letForQuantityInitAnimationRef = React.createRef<boolean>();
    private mousePosition: MousePosition;
    private hideCartTimeout: NodeJS.Timeout | number | null;
    private preventHidingOnTimeout = false;

    constructor(props: CartV1Props) {
        super(props);
        this.mousePosition = { x: 0, y: 0 };
        this.hideCartTimeout = null;

        this.state = {
            isVisible: false,
            isAnimationOn: false,
            cart: null,
            itemsTotalQuantity: 0,
            lastUpdatedItem: '',
            isLoaderActive: false
        };

        this.updateCart = this.updateCart.bind(this);
        this.triggerCartChange = this.triggerCartChange.bind(this);
        this.toggleCart = this.toggleCart.bind(this);
        this.updateTotalQuantity = this.updateTotalQuantity.bind(this);
    }

    componentDidMount(): void {
        window.addEventListener(EventNames.ToggleCart, this.toggleCart);
        window.addEventListener(EventNames.CartChanged, this.triggerCartChange);
        window.addEventListener(EventNames.CartValidated, this.updateCart);
        window.addEventListener(EventNames.ShowUpdatedCart, this.showUpdatedCart);
        window.addEventListener(EventNames.CartTotalQuantityChanged, this.updateTotalQuantity);
        window.addEventListener('mousemove', this.handleMouseMove);
        this.cartContainerRef.current?.addEventListener('click', this.handleClickInsideCart);
        this.cartContainerRef.current?.addEventListener('touchstart', this.handleClickInsideCart, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener(EventNames.ToggleCart, this.toggleCart);
        window.removeEventListener(EventNames.CartChanged, this.triggerCartChange);
        window.removeEventListener(EventNames.CartValidated, this.updateCart);
        window.removeEventListener(EventNames.ShowUpdatedCart, this.showUpdatedCart);
        window.removeEventListener(EventNames.CartTotalQuantityChanged, this.updateTotalQuantity);
        window.removeEventListener('mousemove', this.handleMouseMove);
        this.cartContainerRef.current?.removeEventListener('click', this.handleClickInsideCart);
        this.cartContainerRef.current?.removeEventListener('touchstart', this.handleClickInsideCart);
        if (this.quantityBagdeAnimationTimeoutRef.current) {
            clearTimeout(this.quantityBagdeAnimationTimeoutRef.current);
        }
        if (this.hideHeaderTimeoutRef.current) {
            clearTimeout(this.hideHeaderTimeoutRef.current);
        }
    }

    componentDidUpdate(prevProps: Readonly<CartV1Props>, prevState: Readonly<CartV1State>): void {
        if (
            !this.isMoreThenMaxAmount() &&
            this.state.itemsTotalQuantity > prevState.itemsTotalQuantity &&
            this.quantityBagdeRef.current &&
            this.letForQuantityInitAnimationRef.current
        ) {
            this.quantityBagdeRef.current.classList.add('CartV1__QuantityBadge--PulsatingAnimation');
            if (!prevState.itemsTotalQuantity) {
                this.quantityBagdeRef.current.classList.add('CartV1__QuantityBadge--InitPulsatingAnimation');
            }
            (this.quantityBagdeAnimationTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                if (this.quantityBagdeRef.current) {
                    this.quantityBagdeRef.current.classList.remove('CartV1__QuantityBadge--PulsatingAnimation');
                    this.quantityBagdeRef.current.classList.remove('CartV1__QuantityBadge--InitPulsatingAnimation');
                }
            }, 600);

            if (this.hideHeaderAfterAnimationRef.current) {
                (this.hideHeaderTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                    window.dispatchEvent(new CustomEvent(EventNames.HideHeader));
                    (this.hideHeaderAfterAnimationRef as React.MutableRefObject<boolean>).current = false;
                }, 1200);
            }
        }
    }

    render() {
        const itemsQuantity = this.isMoreThenMaxAmount() ? '99+' : this.state.itemsTotalQuantity;
        return (
            <Swipeable onSwipe={this.handleSwipe}>
                <button
                    className={classNames('CartV1__ToggleButton', {
                        'CartV1__ToggleButton--Disabled': !this.props.displayCart,
                        'CartV1__ToggleButton--LabelVisible': this.props.showButtonLabel
                    })}
                    onClick={() => window.dispatchEvent(new CustomEvent('ToggleCart', { detail: { openedBy: 'icon' } }))}
                    onKeyDown={this.handleKeyboardNavigation}
                >
                    {this.props.showButtonLabel && <span className="hideOnMobile">{this.props.buttonLabel}</span>}
                    {this.state.itemsTotalQuantity > 0 && (
                        <>
                            <div
                                className={classNames('CartV1__QuantityBadge', this.state.itemsTotalQuantity >= 10 && 'CartV1__QuantityBadge--BigCount')}
                                ref={this.quantityBagdeRef}
                            >
                                {itemsQuantity}
                            </div>
                        </>
                    )}
                    <span className="visuallyHidden">{getTranslation(keys['cartSummary.openCart'])}</span>
                </button>

                <FocusTrap enabled={this.state.isVisible}>
                    {this.props.displayCart && (
                        <aside
                            className={classNames('CartV1', !this.state.isVisible && 'CartV1--Hidden', this.state.isAnimationOn && 'CartV1--UpdatedPrice')}
                            ref={this.cartRef}
                            onAnimationEnd={this.onAnimationEnd}
                            onClick={this.handleClickOutside}
                            {...inertHelper(this.state.isVisible)}
                        >
                            <ScrollLock isLocked={this.state.isVisible} />

                            <div className="CartV1__Inner" ref={this.cartContainerRef} onMouseEnter={this.handleMouseIn} onMouseLeave={this.handleMouseOut}>
                                <div className="CartV1__Header">
                                    {getTranslation(keys['cartSummary.cart'])}{' '}
                                    {this.state.itemsTotalQuantity > 0 && (
                                        <span>
                                            {this.state.itemsTotalQuantity}{' '}
                                            {this.state.itemsTotalQuantity > 1
                                                ? getTranslation(keys['cartSummary.products'])
                                                : getTranslation(keys['cartSummary.product'])}
                                        </span>
                                    )}
                                    <button className="CartV1__CloseButton" onClick={this.toggleCart} aria-haspopup="true" aria-expanded={this.state.isVisible}>
                                        <span className="visuallyHidden">{getTranslation(keys['cartSummary.closeCart'])}</span>
                                    </button>
                                </div>
                                {this.state.cart && this.state.itemsTotalQuantity > 0 ? (
                                    <CartSummary
                                        {...this.props}
                                        model={this.state.cart}
                                        setQuantity={this.setQuantity}
                                        removeFromCart={this.removeFromCart}
                                        lastUpdatedItem={this.state.lastUpdatedItem}
                                        resetLastUpdatedItem={this.resetLastUpdatedItem}
                                        isEditable={true}
                                        showFreeDeliveryInfoSection
                                        isLoaderActive={this.state.isLoaderActive}
                                        cartDrawerSettingsPageViewModel={this.props.cartDrawerSettings}
                                    />
                                ) : (
                                    <EmptyCart />
                                )}
                            </div>
                        </aside>
                    )}
                </FocusTrap>
            </Swipeable>
        );
    }

    private handleSwipe = (swipeDirection: SwipeDirection) => {
        if (swipeDirection === SwipeDirection.Right && this.state.isVisible) {
            this.closeCart();
        }
    };

    private handleClickInsideCart = () => {
        if (isMobile()) this.clearHideCartTimeOut();
    };

    private isMoreThenMaxAmount = (): boolean => {
        const maxAmount = 99;

        return this.state.itemsTotalQuantity > maxAmount;
    };

    private handleKeyboardNavigation = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === Key.Enter || e.key === ' ') {
            this.preventHidingOnTimeout = true;
        }
    };

    private clearHideCartTimeOut = () => {
        if (this.hideCartTimeout) {
            clearTimeout(this.hideCartTimeout as number);
        }
    };

    private handleMouseIn = () => {
        this.clearHideCartTimeOut();
    };

    private handleMouseOut = () => {
        this.clearHideCartTimeOut();

        if (this.state.isVisible && !this.preventHidingOnTimeout) {
            this.hideCartTimeout = setTimeout(() => {
                this.closeCart();
            }, 1000);
        }
    };

    private resetLastUpdatedItem = () => {
        this.setState({ lastUpdatedItem: '' });
    };

    private handleMouseMove = (e: MouseEvent) => {
        this.mousePosition = { x: e.clientX, y: e.clientY };
    };

    private validateCart = (cart: CartViewModel) => {
        if (this.state.cart?.id && cart.id !== this.state.cart.id) {
            showFatalErrorModal();
        }

        if (cart?.hasRemovedRxItems) {
            showRxSessionExpiredModal();
        }
    };

    private triggerCartChange($event: CustomEventInit) {
        (this.letForQuantityInitAnimationRef as React.MutableRefObject<boolean>).current = true;
        RecommendationsService.RefreshRecommendationsForWidget(TrackingType.Basket);
        this.updateCart($event);
    }

    private updateCart($event: CustomEventInit) {
        if ($event && $event.detail) {
            const cart = $event.detail.cart as CartViewModel;

            if (cart) {
                this.validateCart(cart);

                CartAccessor.cacheCart(cart);

                this.setState({
                    cart: cart,
                    lastUpdatedItem: $event.detail.lastUpdatedItem,
                    itemsTotalQuantity: cart.totalQuantity
                });
            }
        }
    }

    private updateTotalQuantity($event: CustomEventInit) {
        if ($event && $event.detail) {
            const totalQuantity = $event.detail.totalQuantity as number;

            if (totalQuantity >= 0) {
                this.setState({ itemsTotalQuantity: totalQuantity });
            }
        }
    }

    private showUpdatedCart = async () => {
        const result = await getFor<PersonalizedCartViewModel>(`${this.props.cartUrl}?skipCache=true`);
        (this.letForQuantityInitAnimationRef as React.MutableRefObject<boolean>).current = true;
        this.setState({ cart: result.cart, itemsTotalQuantity: result.cart.totalQuantity });
        const event = new CustomEvent(EventNames.CartChanged, {
            detail: { cart: result.cart, lastUpdatedItem: '' }
        });
        window.dispatchEvent(event);
    };

    private setQuantity = async (productCode: string, quantity: number): Promise<SetQuantityResult> => {
        if (!this.state.cart || quantity <= 0) return SetQuantityResult.failed();

        const url = this.props.cartItemUrl.transformUrl({ productCode });
        const product = this.state.cart.items.find((x) => x.code === productCode);

        if (!product) return SetQuantityResult.failed();

        return await setQuantity(productCode, product, quantity, url, this.state.cart, (cart: CartViewModel) => {
            this.validateCart(cart);
            this.setState({ cart: cart, itemsTotalQuantity: cart.totalQuantity });
        });
    };

    private removeFromCart = (productCode: string): Promise<boolean> => {
        if (!this.state.cart) throw Error('Tried to remove item from empty cart.');
        this.setState({ isLoaderActive: true });
        const url = this.props.cartItemUrl.transformUrl({ productCode });
        const oldRxItemsCount = this.state.cart.items.filter((item) => item.isRx).length;

        return removeFromCart(productCode, url, this.state.cart, (cart: CartViewModel) => {
            const newRxItemsCount = cart.items.filter((item) => item.isRx).length;
            if (oldRxItemsCount != newRxItemsCount) {
                window.dispatchEvent(new CustomEvent(EventNames.RxRemovedFromCart));
            }

            this.validateCart(cart);
            this.setState({
                cart: cart,
                isAnimationOn: true,
                itemsTotalQuantity: cart.totalQuantity,
                isLoaderActive: false
            });
        });
    };

    private onAnimationEnd = () => {
        this.setState({ isAnimationOn: false });
    };

    private handleClickOutside = (event: React.MouseEvent<HTMLElement>) => {
        const cartContainer = this.cartContainerRef.current;

        if (cartContainer && !cartContainer.contains(event.target as Node)) {
            this.closeCart();
        }
    };

    private toggleCart($event: CustomEventInit) {
        const defaultCartWidth = 490;

        const cartWidth = this.cartContainerRef.current ? this.cartContainerRef.current.offsetWidth : defaultCartWidth;

        if (
            !this.state.isVisible &&
            (window.innerWidth - this.mousePosition.x >= cartWidth || (isMobile() && $event?.detail?.openedBy !== 'icon')) &&
            !this.preventHidingOnTimeout
        ) {
            this.hideCartTimeout = setTimeout(() => {
                this.closeCart();
            }, 5000);
        }

        if ($event && $event.detail && $event.detail.open !== undefined) {
            return this.handleToggleCart($event.detail.open);
        }

        this.handleToggleCart(!this.state.isVisible);
    }

    private handleToggleCart = (isOpen: boolean) => {
        if (isOpen) {
            this.setState({ isVisible: true });

            if (this.state.cart) GoogleTagManager.cartView(this.state.cart.items, this.state.cart.total, this.state.cart.coupon, true);
            window.dispatchEvent(new CustomEvent(EventNames.CloseSearch));
            window.dispatchEvent(new CustomEvent(EventNames.CloseNavigation));
        } else {
            this.closeCart();
        }
    };

    private closeCart() {
        this.clearHideCartTimeOut();
        this.setState({ isVisible: false, lastUpdatedItem: '' });
        this.preventHidingOnTimeout = false;
    }
}
